import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownCircle,
  Boxes,
  History,
  Loader2,
  PackagePlus,
  Plus,
  Search,
  Sparkles,
  Tag,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl, dataBR, fileUrl, gramas } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Field, Loading, PageHeader, StockBadge, Surface, Thumb } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { ImageUpload } from "@/components/ds/ImageUpload";
import { FilamentPicker } from "@/components/ds/FilamentPicker";

const EMPTY_COLOR = {
  brand: "",
  material: "PLA",
  color_name: "",
  manufacturer_color: "",
  color_hex: "",
  supplier: "",
  min_stock_g: "500",
  initial_qty_g: "1000",
  paid_value: "",
  price_per_kg: "",
  notes: "",
  photo_path: null,
};

const MOV_LABEL = { ENTRADA: "Entrada", CONSUMO: "Consumo", AJUSTE: "Ajuste" };

export default function Filaments({ embedded = false }) {
  const [filaments, setFilaments] = useState(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [newColorOpen, setNewColorOpen] = useState(false);
  const [entryOpen, setEntryOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [priceFor, setPriceFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/filaments");
      setFilaments(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = useMemo(() => {
    const t = query.trim().toLowerCase();
    if (!t || !filaments) return filaments || [];
    return filaments.filter((f) =>
      [f.color_name, f.brand, f.material, f.manufacturer_color, f.supplier]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(t)
    );
  }, [filaments, query]);

  const saveHex = async (f, hex) => {
    try {
      await api.put(`/filaments/${f.id}`, {
        brand: f.brand,
        material: f.material,
        color_name: f.color_name,
        manufacturer_color: f.manufacturer_color || "",
        color_hex: hex,
        supplier: f.supplier || "",
        min_stock_g: f.min_stock_g || 0,
        photo_path: f.photo_path || null,
        active: f.active !== false,
        notes: f.notes || "",
      });
      setFilaments((prev) => prev.map((x) => (x.id === f.id ? { ...x, color_hex: hex } : x)));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/filaments/${toDelete.id}`);
      toast.success("Filamento excluído.");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setToDelete(null);
    }
  };

  const NewButton = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="new-filament-btn"
          className="h-11 gap-2 rounded-full px-5 font-semibold transition-transform active:scale-95"
        >
          <Plus className="h-4 w-4" /> Novo
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 border-white/10 bg-popover">
        <DropdownMenuItem
          onClick={() => setNewColorOpen(true)}
          data-testid="menu-new-color"
          className="cursor-pointer gap-3 py-3"
        >
          <Sparkles className="h-4 w-4 text-brand" />
          <div>
            <p className="text-sm font-medium">Nova Cor</p>
            <p className="text-xs text-muted-foreground">Ainda não tenho esse filamento</p>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setEntryOpen(true)}
          data-testid="menu-stock-entry"
          className="cursor-pointer gap-3 py-3"
        >
          <PackagePlus className="h-4 w-4 text-emerald-400" />
          <div>
            <p className="text-sm font-medium">Entrada de Estoque</p>
            <p className="text-xs text-muted-foreground">Adicionar quantidade a um filamento</p>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-8" data-testid="filaments-page">
      {embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="max-w-2xl text-sm text-muted-foreground">
            Todo o controle é feito em gramas. Não importa se está em um rolo, dois rolos ou em
            sobras.
          </p>
          {NewButton}
        </div>
      ) : (
        <PageHeader
          title="Estoque de Filamentos"
          subtitle="Todo o controle é feito em gramas. Não importa se está em um rolo, dois rolos ou em sobras."
          action={NewButton}
        />
      )}

      {filaments === null && !error && <Loading label="Carregando seus filamentos..." />}
      {error && <ErrorState message={error} onRetry={load} />}

      {filaments && filaments.length === 0 && (
        <EmptyState
          icon={Boxes}
          title="Nenhum filamento cadastrado"
          description="Comece cadastrando sua primeira cor."
          action={
            <Button onClick={() => setNewColorOpen(true)} data-testid="empty-new-color-btn" className="gap-2 rounded-full">
              <Plus className="h-4 w-4" /> Nova Cor
            </Button>
          }
        />
      )}

      {filaments && filaments.length > 0 && (
        <>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar filamento"
              className="pl-9"
              data-testid="filament-search-input"
            />
          </div>

          {list.length === 0 ? (
            <EmptyState title="Nada encontrado" description="Tente buscar por outra cor, marca ou material." />
          ) : (
            <div className="grid gap-4 stagger md:grid-cols-2 xl:grid-cols-3 [&>*]:min-w-0">
              {list.map((f) => (
                <Surface key={f.id} className="p-5" data-testid={`filament-card-${f.id}`}>
                  <div className="flex items-start gap-4">
                    <Thumb
                      src={fileUrl(f.photo_path)}
                      alt={f.color_name}
                      className="h-16 w-16"
                      fallback={f.material?.slice(0, 3)}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-display text-base font-medium" data-testid={`filament-name-${f.id}`}>
                        {f.material} {f.color_name}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {f.brand}
                        {f.supplier ? ` · ${f.supplier}` : ""}
                      </p>
                      <p
                        className="mt-3 font-num text-2xl font-medium"
                        data-testid={`filament-balance-${f.id}`}
                      >
                        {gramas(f.balance_g)}
                      </p>
                      <p className="text-xs text-muted-foreground">disponíveis</p>
                      <CostSourceLine filament={f} />
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="color"
                          value={f.color_hex || "#cccccc"}
                          onChange={(e) => saveHex(f, e.target.value)}
                          data-testid={`filament-hex-${f.id}`}
                          className="h-7 w-10 cursor-pointer rounded border border-input bg-background p-0.5"
                        />
                        <span className="text-[11px] text-muted-foreground">
                          {f.color_hex ? "Cor do catálogo" : "Definir cor do catálogo"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-4">
                    <StockBadge low={f.low_stock} testid={`filament-status-${f.id}`} />
                    <div className="flex flex-wrap items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPriceFor(f)}
                        data-testid={`filament-price-btn-${f.id}`}
                        className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Tag className="h-3.5 w-3.5" /> Preço/kg
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryFor(f)}
                        data-testid={`filament-history-btn-${f.id}`}
                        className="gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <History className="h-3.5 w-3.5" /> Histórico
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setToDelete(f)}
                        data-testid={`filament-delete-btn-${f.id}`}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Estoque mínimo: <span className="font-num">{gramas(f.min_stock_g)}</span>
                  </p>
                </Surface>
              ))}
            </div>
          )}
        </>
      )}

      <NewColorModal open={newColorOpen} onOpenChange={setNewColorOpen} onSaved={load} />
      <StockEntryModal
        open={entryOpen}
        onOpenChange={setEntryOpen}
        filaments={filaments || []}
        onSaved={load}
      />
      <HistoryModal filament={historyFor} onClose={() => setHistoryFor(null)} />
      {priceFor && (
        <PricePerKgModal
          filament={priceFor}
          onClose={() => setPriceFor(null)}
          onSaved={load}
        />
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-filament-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir filamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Todo o histórico de movimentações de{" "}
              <strong>
                {toDelete?.material} {toDelete?.color_name}
              </strong>{" "}
              será removido. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/* ---------------------------------------------- Prévia do custo da entrada */
/** Mostra o custo equivalente por kg da entrada e alerta divergências. Nunca bloqueia. */
function EntryCostPreview({ filament: f, grams, paid }) {
  if (!grams || !paid) return null;
  const perKg = (paid / grams) * 1000;
  const ref = Number(f.price_per_kg || 0);
  const diffPct = ref > 0 ? ((perKg - ref) / ref) * 100 : null;
  const diverges = diffPct != null && Math.abs(diffPct) > 10;
  // Sem preço de referência, o valor desta entrada vira o custo: avisa o caso clássico
  // de lançar o preço do rolo cheio junto com o saldo recebido.
  const partialRoll = diffPct == null && grams < 1000;
  const extreme = diffPct == null && (perKg < 30 || perKg > 200);
  const tone = diverges || extreme || partialRoll
    ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
    : "border-white/10 bg-secondary/40 text-muted-foreground";
  return (
    <div className={`rounded-lg border px-4 py-3 text-xs ${tone}`} data-testid="entry-cost-preview">
      <p>
        Custo equivalente desta entrada:{" "}
        <span className="font-num text-foreground" data-testid="entry-cost-per-kg">
          {brl(perKg)} / kg
        </span>
        {ref > 0 && <> · referência cadastrada: <span className="font-num">{brl(ref)} / kg</span></>}
      </p>
      {diverges && (
        <p className="mt-1" data-testid="entry-cost-warning">
          Divergência de {diffPct > 0 ? "+" : ""}
          {diffPct.toFixed(0)}% em relação ao preço por kg cadastrado. Confira se o valor pago é
          o do rolo inteiro e a quantidade é só o saldo recebido. Se for material fracionado,
          pode confirmar: o custo dos cálculos continua usando o preço por kg.
        </p>
      )}
      {extreme && (
        <p className="mt-1" data-testid="entry-cost-warning">
          Esse valor por kg está fora do normal. Confira o valor pago e as gramas — ou cadastre o
          preço por kg deste filamento para o custo não depender desta conta.
        </p>
      )}
      {partialRoll && !extreme && (
        <p className="mt-1" data-testid="entry-cost-warning">
          Esta entrada tem {gramas(grams)} e este filamento não tem preço por kg cadastrado:
          é este valor que vai virar o custo dos cálculos. Se os {brl(paid)} são o preço do rolo
          inteiro de 1 kg, cadastre o preço por kg. Se é material fracionado mesmo, pode
          confirmar.
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------- Fonte do custo e Preço/kg */
const COST_SOURCE_LABEL = {
  PRECO_KG: "preço por kg cadastrado",
  MEDIA_ENTRADAS: "média das entradas",
  SEM_CUSTO: "sem custo registrado",
};

function CostSourceLine({ filament: f }) {
  return (
    <p className="mt-1 text-xs text-muted-foreground" data-testid={`filament-cost-${f.id}`}>
      {f.cost_source === "SEM_CUSTO" ? (
        <span className="text-amber-300">Sem custo registrado</span>
      ) : (
        <>
          <span className="font-num text-foreground">{brl(f.cost_per_kg)}</span> / kg ·{" "}
          {COST_SOURCE_LABEL[f.cost_source]}
        </>
      )}
    </p>
  );
}

/** Preço de referência do material. Vazio = volta a usar a média das entradas. */
function PricePerKgModal({ filament: f, onClose, onSaved }) {
  const [value, setValue] = useState(f.price_per_kg == null ? "" : String(f.price_per_kg));
  const [busy, setBusy] = useState(false);
  const perG = Number(value || 0) / 1000;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/filaments/${f.id}`, {
        brand: f.brand,
        material: f.material,
        color_name: f.color_name,
        manufacturer_color: f.manufacturer_color || "",
        color_hex: f.color_hex || "",
        supplier: f.supplier || "",
        min_stock_g: f.min_stock_g || 0,
        price_per_kg: value === "" ? null : Number(value),
        photo_path: f.photo_path || null,
        active: f.active !== false,
        notes: f.notes || "",
      });
      toast.success("Preço por kg atualizado.");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title="Preço por kg"
      description={`${f.material} ${f.color_name}`}
      testid="price-per-kg-modal"
    >
      <div className="mt-2 space-y-4">
        <Field label="Preço por kg (R$)" hint="Deixe vazio para usar a média das entradas">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="89,90"
            className="font-num"
            data-testid="price-per-kg-input"
          />
        </Field>
        <p
          className="rounded-lg border border-white/10 bg-secondary/40 px-4 py-3 text-xs"
          data-testid="price-per-kg-preview"
        >
          {Number(value) > 0 ? (
            <>
              Custo por grama: <span className="font-num text-foreground">R$ {perG.toFixed(4)}</span>
              {" "}· passa a ser a fonte de custo nos novos cálculos.
            </>
          ) : (
            <>
              Sem preço por kg: segue a média das entradas
              {f.cost_source === "MEDIA_ENTRADAS" ? ` (${brl(f.cost_per_kg)} / kg hoje)` : ""}.
            </>
          )}
          {" "}O saldo físico ({gramas(f.balance_g)}) não muda.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1 rounded-full"
                  data-testid="price-per-kg-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={save} disabled={busy} className="flex-1 rounded-full"
                  data-testid="price-per-kg-save-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
}

/* ------------------------------------------------------------------ Nova Cor */
function NewColorModal({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(EMPTY_COLOR);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) setForm(EMPTY_COLOR);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(form.price_per_kg) > 0)) {
      toast.error("Informe o preço por kg (R$) do filamento. É o preço de 1 kg do material.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/filaments", {
        brand: form.brand,        material: form.material,
        color_name: form.color_name,
        manufacturer_color: form.manufacturer_color,
        color_hex: form.color_hex || "",
        supplier: form.supplier,
        min_stock_g: Number(form.min_stock_g || 0),
        initial_qty_g: Number(form.initial_qty_g || 0),
        paid_value: form.paid_value === "" ? null : Number(form.paid_value),
        price_per_kg: form.price_per_kg === "" ? null : Number(form.price_per_kg),
        notes: form.notes,
        photo_path: form.photo_path,
        active: true,
      });
      toast.success("Filamento cadastrado com sucesso.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Nova Cor"
      description="Cadastre um filamento que você ainda não tem no sistema."
      testid="new-color-modal"
    >
      <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
        <ImageUpload value={form.photo_path} onChange={(p) => setForm((f) => ({ ...f, photo_path: p }))} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Marca">
            <Input value={form.brand} onChange={set("brand")} required placeholder="Ex.: F3D" data-testid="color-brand-input" />
          </Field>
          <Field label="Material">
            <Input value={form.material} onChange={set("material")} required placeholder="Ex.: PLA" data-testid="color-material-input" />
          </Field>
          <Field label="Cor comercial">
            <Input value={form.color_name} onChange={set("color_name")} required placeholder="Ex.: Rosa Bebê" data-testid="color-name-input" />
          </Field>
          <Field label="Cor do fabricante">
            <Input value={form.manufacturer_color} onChange={set("manufacturer_color")} placeholder="Ex.: Rosa Bebê" data-testid="color-manufacturer-input" />
          </Field>
          <Field label="Cor real (mostrada no catálogo)" hint="Usada nas bolinhas de cor da personalização">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color_hex || "#cccccc"}
                onChange={set("color_hex")}
                data-testid="color-hex-input"
                className="h-10 w-14 cursor-pointer rounded-md border border-input bg-background p-1"
              />
              <Input
                value={form.color_hex || ""}
                onChange={set("color_hex")}
                placeholder="#FF66A1"
                className="font-num"
                data-testid="color-hex-text-input"
              />
            </div>
          </Field>
          <Field label="Fornecedor">
            <Input value={form.supplier} onChange={set("supplier")} placeholder="Ex.: Loja XYZ" data-testid="color-supplier-input" />
          </Field>
          <Field label="Estoque mínimo (g)">
            <Input type="number" min="0" value={form.min_stock_g} onChange={set("min_stock_g")} className="font-num" data-testid="color-min-stock-input" />
          </Field>
          <Field label="Quantidade disponível (g)" hint="Quantidade física que você realmente tem hoje">
            <Input type="number" min="0" value={form.initial_qty_g} onChange={set("initial_qty_g")} className="font-num" data-testid="color-initial-qty-input" />
          </Field>
          <Field label="Valor pago (R$)" hint="Opcional: o que você pagou por essa quantidade">
            <Input type="number" min="0" step="0.01" value={form.paid_value} onChange={set("paid_value")} placeholder="89,90" className="font-num" data-testid="color-paid-value-input" />
          </Field>
          <Field
            label="Preço por kg (R$) *"
            className="sm:col-span-2"
          >
            <Input type="number" min="0.01" step="0.01" value={form.price_per_kg} onChange={set("price_per_kg")} placeholder="89,90" className="font-num" data-testid="color-price-per-kg-input" />
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid="color-price-per-kg-preview">
              {Number(form.price_per_kg) > 0
                ? `Custo de referência: R$ ${(Number(form.price_per_kg) / 1000).toFixed(4).replace(".", ",")} / g · a quantidade disponível continua sendo só o que você tem.`
                : "Obrigatório: é esse valor que define o custo dos produtos. Não divida pelo que resta no rolo."}
            </p>
          </Field>
        </div>
        <Field label="Observações">
          <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="color-notes-input" />
        </Field>
        <Button type="submit" disabled={busy} data-testid="save-color-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
        </Button>
      </form>
    </ResponsiveModal>
  );
}

/* ------------------------------------------------------- Entrada de Estoque */
function StockEntryModal({ open, onOpenChange, filaments, onSaved }) {
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ quantity_g: "", paid_value: "", supplier: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (open) {
      setSelected(null);
      setForm({ quantity_g: "", paid_value: "", supplier: "", notes: "" });
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/filament-movements", {
        filament_id: selected.id,
        type: "ENTRADA",
        quantity_g: Number(form.quantity_g),
        paid_value: form.paid_value === "" ? null : Number(form.paid_value),
        supplier: form.supplier,
        notes: form.notes,
      });
      toast.success("Entrada registrada no estoque.");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const added = Number(form.quantity_g || 0);

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title="Entrada de Estoque"
      description={selected ? "Informe a quantidade recebida." : "Escolha o filamento que você recebeu."}
      testid="stock-entry-modal"
    >
      {!selected ? (
        filaments.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            Você ainda não tem filamentos. Cadastre uma Nova Cor primeiro.
          </p>
        ) : (
          <div className="mt-2">
            <FilamentPicker filaments={filaments} onSelect={setSelected} testid="entry-filament-picker" />
          </div>
        )
      ) : (
        <form onSubmit={submit} className="mt-2 flex flex-col gap-4">
          <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-secondary/40 p-3">
            <Thumb src={fileUrl(selected.photo_path)} alt={selected.color_name} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {selected.material} {selected.color_name}
              </p>
              <p className="text-xs text-muted-foreground">
                Estoque atual: <span className="font-num">{gramas(selected.balance_g)}</span>
              </p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="change-filament-btn" className="text-xs">
              Trocar
            </Button>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Entrada (g)">
              <Input type="number" min="1" required value={form.quantity_g} onChange={set("quantity_g")} className="font-num" data-testid="entry-quantity-input" />
            </Field>
            <Field label="Valor pago (R$)">
              <Input type="number" min="0" step="0.01" value={form.paid_value} onChange={set("paid_value")} className="font-num" data-testid="entry-paid-value-input" />
            </Field>
            <Field label="Fornecedor" className="sm:col-span-2">
              <Input value={form.supplier} onChange={set("supplier")} data-testid="entry-supplier-input" />
            </Field>
          </div>
          <Field label="Observação">
            <Textarea value={form.notes} onChange={set("notes")} rows={2} data-testid="entry-notes-input" />
          </Field>

          <div className="flex items-center justify-between rounded-lg border border-brand/30 bg-brand/10 px-4 py-3 text-sm">
            <span className="text-muted-foreground">Novo estoque</span>
            <span className="font-num text-lg text-brand" data-testid="entry-new-balance">
              {gramas(Number(selected.balance_g || 0) + added)}
            </span>
          </div>
          <EntryCostPreview filament={selected} grams={added} paid={Number(form.paid_value || 0)} />

          <Button type="submit" disabled={busy} data-testid="save-entry-btn" className="h-11 rounded-full font-semibold transition-transform active:scale-95">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar entrada
          </Button>
        </form>
      )}
    </ResponsiveModal>
  );
}

/* -------------------------------------------------------------- Histórico */
function HistoryModal({ filament, onClose }) {
  const [moves, setMoves] = useState(null);

  useEffect(() => {
    if (!filament) return;
    setMoves(null);
    api
      .get(`/filaments/${filament.id}/history`)
      .then(({ data }) => setMoves(data))
      .catch((e) => toast.error(apiError(e)));
  }, [filament]);

  return (
    <ResponsiveModal
      open={!!filament}
      onOpenChange={(o) => !o && onClose()}
      title="Histórico de movimentações"
      description={filament ? `${filament.material} ${filament.color_name}` : ""}
      testid="history-modal"
    >
      {moves === null ? (
        <Loading label="Carregando histórico..." testid="history-loading" />
      ) : moves.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground" data-testid="history-empty">
          Nenhuma movimentação registrada ainda.
        </p>
      ) : (
        <ul className="mt-2 divide-y divide-white/10" data-testid="history-list">
          {moves.map((m) => (
            <li key={m.id} className="flex items-center gap-3 py-3" data-testid={`history-item-${m.id}`}>
              <span className="w-14 shrink-0 font-num text-xs text-muted-foreground">{dataBR(m.created_at)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{MOV_LABEL[m.type] || m.type}</p>
                {(m.notes || m.supplier) && (
                  <p className="truncate text-xs text-muted-foreground">
                    {[m.supplier, m.notes].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className={`font-num text-sm ${m.quantity_g >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                  {m.quantity_g >= 0 ? "+" : "−"}
                  {gramas(Math.abs(m.quantity_g))}
                </p>
                {m.paid_value != null && (
                  <p className="font-num text-xs text-muted-foreground">{brl(m.paid_value)}</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
        <ArrowDownCircle className="h-3.5 w-3.5" /> O saldo é sempre calculado somando estas movimentações.
      </p>
    </ResponsiveModal>
  );
}
